import React, { useEffect, useState, FunctionComponent, useRef } from 'react'
import LivedayLoader from './animations/LivedayLoader'

// Define the props type
interface OrganizerArenaProps {
  url: string
  setActive?: (elementId: string) => void
  clickableSections?: string[]
  blockedSections?: string[]
  poolSections?: string[]
}

const OrganizerArena: FunctionComponent<OrganizerArenaProps> = ({
  url,
  setActive,
  clickableSections,
  blockedSections,
  poolSections,
}) => {
  const [svgContent, setSvgContent] = useState<SVGElement | null>(null)
  const svgContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetch(`${process.env.NEXT_PUBLIC_STATIC_URL}/${url}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return response.text()
      })
      .then((data) => {
        const parser = new DOMParser()
        const svgDoc = parser.parseFromString(data, 'image/svg+xml')
        setSvgContent(svgDoc.documentElement as unknown as SVGElement)
      })
      .catch((error) => {
        console.error('Error fetching SVG:', error)
      })
  }, [url])

  useEffect(() => {
    const onElementClick = (element: Element) => {
      if (!setActive) return
      setActive(element.id)
    }

    const clickListeners = new Map() // Store listeners

    const addClickListeners = () => {
      const svgElement = svgContainerRef.current?.querySelector('svg')
      if (!svgElement) return

      const elements = svgElement.querySelectorAll('*')
      elements.forEach((element) => {
        if (element.querySelector('*')) return // Skip if the element has SVG children

        const addToElement = (el: Element) => {
          const clickListener = () => onElementClick(el)
          clickListeners.set(el, clickListener) // Store the listener
          el.addEventListener('click', clickListener)
          el.classList.add('cursor-pointer', 'svg-hover')
        }

        if (clickableSections && clickableSections.includes(element.id)) {
          addToElement(element)
        } else if (!clickableSections) {
          addToElement(element)
        }
      })
    }

    const removeClickListeners = () => {
      const svgElement = svgContainerRef.current?.querySelector('svg')
      if (!svgElement) return

      svgElement.querySelectorAll('*').forEach((element) => {
        if (element.querySelector('*')) return // Skip if the element has SVG children
        const clickListener = clickListeners.get(element)
        if (clickListener) {
          element.removeEventListener('click', clickListener)
          element.classList.remove('cursor-pointer', 'svg-hover')
        }
      })
    }

    addClickListeners()

    // Cleanup function to remove event listeners
    return () => removeClickListeners()
  }, [svgContent, setActive, clickableSections])

  useEffect(() => {
    const addBlockedSections = () => {
      const svgElement = svgContainerRef.current?.querySelector('svg')
      if (!svgElement) return

      blockedSections?.forEach((section) => {
        const element = svgElement.querySelector(`#${section}`)
        if (element) {
          element.classList.add('svg-blocked')
        }
      })
    }

    const removeBlockedSections = () => {
      const svgElement = svgContainerRef.current?.querySelector('svg')
      if (!svgElement) return

      blockedSections?.forEach((section) => {
        const element = svgElement.querySelector(`#${section}`)
        if (element) {
          element.classList.remove('svg-blocked')
        }
      })
    }

    addBlockedSections()

    // Cleanup function to remove event listeners
    return () => removeBlockedSections()
  }, [svgContent, blockedSections])

  useEffect(() => {
    const addPoolSections = () => {
      const svgElement = svgContainerRef.current?.querySelector('svg')
      if (!svgElement) return

      poolSections?.forEach((section) => {
        const element = svgElement.querySelector(`#${section}`)
        if (element) {
          element.classList.add('svg-pool')
        }
      })
    }

    const removePoolSections = () => {
      const svgElement = svgContainerRef.current?.querySelector('svg')
      if (!svgElement) return

      poolSections?.forEach((section) => {
        const element = svgElement.querySelector(`#${section}`)
        if (element) {
          element.classList.remove('svg-pool')
        }
      })
    }

    addPoolSections()

    // Cleanup function to remove event listeners
    return () => removePoolSections()
  }, [svgContent, poolSections])

  useEffect(() => {
    const svgElement = svgContainerRef.current?.querySelector('svg')
    if (svgElement) {
      svgElement.classList.add('h-full', 'w-full')
    }
    return () => {
      if (svgElement) {
        svgElement.classList.remove('h-full', 'w-full')
      }
    }
  }, [svgContent])

  if (svgContent === null) {
    return <LivedayLoader />
  }

  return (
    <div
      ref={svgContainerRef}
      dangerouslySetInnerHTML={{ __html: svgContent?.outerHTML || '' }}
    />
  )
}

export default OrganizerArena
